import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APPROVE_BATCH, isSubAgent, type ToolCard } from '../../types/agent';
import { AgentTag } from './agentLabel';
import { toolLabel } from '../../services/agent/toolCatalogue';
import { qualify } from '../../services/excel/a1';
import { Collapse, ERROR_HOLD_MS, useDeparting, useLingering } from './motion';
import { BatchApprovalCard } from './BatchApprovalCard';

export interface ActionCardActions {
  onDecide: (toolCallId: string, approved: boolean, opts?: { overwrite_ack?: boolean; message?: string }) => void;
  onShow: (card: ToolCard) => void;
  onUndo: (card: ToolCard) => void;
}

const keyOf = (c: ToolCard) => c.tool_call_id;
const notFailed = (c: ToolCard) => c.status !== 'error';
const errorHold = () => ERROR_HOLD_MS;

/** The action cards of a segment. One that settled applied, discarded or reverted stays as its one-liner; one
 *  that failed shows its error, in amber, for as long as a failed step does (`ERROR_HOLD_MS`), then collapses
 *  away: the agent reads the failure and says in its answer what it did about it, so the pane keeps no error
 *  on screen. A reloaded thread never shows it (`useDeparting` renders nothing that was hidden when it
 *  mounted). */
export function ActionCards({ cards, actions }: { cards: ToolCard[]; actions: ActionCardActions }) {
  const rendered = useDeparting(cards, keyOf, notFailed, errorHold);
  return (
    <>
      {rendered.map(({ item, phase }) => (
        <Collapse key={item.tool_call_id} leaving={phase === 'leaving'}>
          {item.tool_name === APPROVE_BATCH ? <BatchApprovalCard card={item} actions={actions} /> : <ActionCard card={item} actions={actions} />}
        </Collapse>
      ))}
    </>
  );
}

/** Design 1b "action card": a client tool call that needs the user's approval — title (server
 *  preview), mono target, compact rendering of the input, then Apply / Discard → Applied · N cells
 *  + Undo, Discarded, Reverted or the error. */
export function ActionCard({ card, actions }: { card: ToolCard; actions: ActionCardActions }) {
  const { t } = useTranslation();
  const target = card.target ? qualify(card.target.sheet, card.target.address) : null;
  const canShow = !!card.target && (card.status === 'done' || card.status === 'error');
  const settled = card.status === 'done' || card.status === 'cancelled' || card.status === 'error';
  const overwriteCells = card.overwrite?.cells ?? 0;
  const [ack, setAck] = useState(false);
  const body = (
    <div className={`overflow-hidden rounded-lg border bg-white ${card.risk === 'destructive' && card.status === 'awaiting_confirmation' ? 'border-[#e8c9c5]' : 'border-line-card'}`}>
      <div className="flex items-center justify-between gap-2 border-b border-line-row px-3 py-[9px]">
        <span className="min-w-0 truncate text-[12px] font-medium text-ink" title={card.preview || undefined}>
          {card.preview || toolLabel(card.tool_name)}
        </span>
        {isSubAgent(card.agent) && <AgentTag agent={card.agent} t={t} />}
        {target && (
          <button type="button" disabled={!canShow} onClick={() => actions.onShow(card)} className={`shrink-0 font-mono text-[11px] text-muted ${canShow ? 'hover:text-accent' : 'cursor-default'}`} title={canShow ? t('action.show') : undefined}>
            {target}
          </button>
        )}
      </div>
      <pre className="m-0 whitespace-pre-wrap break-all bg-code px-3 py-2.5 font-mono text-[11.5px] leading-[1.55] text-[#2d3134]">{describeInput(card)}</pre>
      {card.risk === 'destructive' && card.status === 'awaiting_confirmation' && <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-danger">{t('action.destructiveWarning')}</div>}
      {card.protected && card.status === 'awaiting_confirmation' && <div className="border-t border-line-row px-3 py-1.5 text-[11px] text-danger">{t('action.protectedSheet')}</div>}
      {overwriteCells > 0 && card.status === 'awaiting_confirmation' && (
        <label className="flex cursor-pointer items-start gap-2 border-t border-line-row px-3 py-1.5 text-[11.5px] text-ink">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
          <span>{t('action.overwriteAck', { count: overwriteCells, sample: (card.overwrite?.sample ?? []).join(', ') })}</span>
        </label>
      )}
      {/* Keyed on the status so Apply → Applying… cross-fades (the new footer mounts with pc-in). */}
      <div key={card.status} className="flex animate-pc-in-fast items-center gap-1.5 border-t border-line-row p-2">
        <Footer card={card} actions={actions} ack={ack} needsAck={overwriteCells > 0} />
      </div>
    </div>
  );
  // Once settled the body collapses out (kept for LEAVE_MS by useLingering) while the one-liner
  // pops in: Applied · N cells · Undo, Discarded, Reverted, or the error (product feedback 2026-09-05).
  const lingering = useLingering(!settled, body);
  if (settled) {
    return (
      <div className="flex flex-col" data-card-status={card.status}>
        {lingering && <Collapse leaving>{lingering}</Collapse>}
        <div className={`flex min-h-[26px] animate-pc-in-enter items-center gap-2 rounded-md px-2 py-1 text-[12px] ${card.status === 'error' ? 'bg-warn-bg' : 'bg-code'}`} title={card.preview || toolLabel(card.tool_name)}>
          <span className="min-w-0 flex-1 truncate">
            <Footer card={card} actions={actions} compact />
          </span>
          {isSubAgent(card.agent) && <AgentTag agent={card.agent} t={t} />}
          {target && (
            <button type="button" disabled={!canShow} onClick={() => actions.onShow(card)} className={`shrink-0 font-mono text-[11px] text-faint ${canShow ? 'hover:text-accent' : 'cursor-default'}`} title={canShow ? t('action.show') : undefined}>
              {target}
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="animate-pc-in-enter" data-card-status={card.status}>
      {body}
    </div>
  );
}

function Footer({ card, actions, compact = false, ack = false, needsAck = false }: { card: ToolCard; actions: ActionCardActions; compact?: boolean; ack?: boolean; needsAck?: boolean }) {
  const { t } = useTranslation();
  const pad = compact ? '' : 'px-1 py-1.5';
  switch (card.status) {
    case 'awaiting_confirmation':
      return (
        <>
          <button type="button" disabled={needsAck && !ack} onClick={() => actions.onDecide(card.tool_call_id, true, needsAck ? { overwrite_ack: ack } : undefined)} className="flex-1 rounded-[5px] bg-accent py-[7px] text-center text-[12.5px] font-medium text-white transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40">
            {t('action.apply')}
          </button>
          <button type="button" onClick={() => actions.onDecide(card.tool_call_id, false)} className="rounded-[5px] border border-[#dcdcd8] px-3 py-[7px] text-[12.5px] text-[#4a4f54] transition-colors hover:bg-hover">
            {t('action.discard')}
          </button>
        </>
      );
    case 'pending':
    case 'running':
      return (
        <div className="flex flex-1 items-center gap-2 px-1 py-1.5 text-[12px] text-muted">
          <div className="h-3 w-3 animate-pc-spin rounded-full border-[1.5px] border-accent border-t-transparent" />
          {t('action.applying')}
        </div>
      );
    case 'done':
      if (card.undone) return <span className={`text-[12px] text-faint ${pad}`}>{t('action.reverted')}</span>;
      return (
        <span className={`inline-flex items-center gap-1.5 text-[12px] text-accent-dark ${pad}`}>
          <span className="inline-block animate-pc-pop">✓</span> {t('action.applied')} · {appliedLabel(card, t)}
          {card.journal_id && (
            <>
              <span className="text-line">·</span>
              <button type="button" onClick={() => actions.onUndo(card)} className="rounded-sm px-1 text-[12px] text-muted transition-colors hover:bg-hover hover:text-ink">
                {t('action.undo')}
              </button>
            </>
          )}
        </span>
      );
    case 'cancelled':
      return <span className={`text-[12px] text-faint ${pad}`}>{t('action.discarded')}</span>;
    case 'error':
      return (
        <span className={`text-[12px] leading-snug text-warn ${pad}`}>
          {t('action.failed')}
          {card.error ? `: ${card.error}` : ''}
          {card.hint ? <span className="text-faint"> — {card.hint}</span> : null}
        </span>
      );
    default:
      return null;
  }
}

type T = (key: string, opts?: Record<string, unknown>) => string;

/** "1 cell" / "14 cells" / "1 sheet" / "1 chart"… from `_changes` or the tool name. */
export function appliedLabel(card: ToolCard, t: T): string {
  const cells = card.changes?.cells;
  if (typeof cells === 'number' && cells > 0) return t('action.cells', { count: cells });
  const n = card.tool_name;
  if (n === 'excel_converge') return t('action.converged');
  if (n === 'excel_insert_model') return t('action.newModel');
  if (/_sheet$/.test(n) || n === 'excel_set_sheet_visibility') return t('action.oneSheet');
  if (/chart$/.test(n)) return t('action.oneChart');
  if (/table/.test(n)) return t('action.oneTable');
  if (/_name$/.test(n)) return t('action.oneName');
  return t('action.done');
}

/** Compact, mono rendering of the tool input for the card body.
 *
 *  The cases below are sculpt-api's write tools (`sculpt/agent/tools/excel.py`). Three of the old ones —
 *  `excel_write_block`, `excel_write_range`, `excel_fill_range` — no longer exist, and `excel_write_cells`,
 *  which is the one a Change Plan uses most, had no case at all: it fell through to the generic dump and a
 *  write of nine cells was rendered as `cells: {"J110":"=R[-3]C*1.02","J111":…` on one unbroken line. */
export function describeInput(card: ToolCard): string {
  const input = card.input ?? {};
  const n = card.tool_name;
  if (n === 'excel_write_formulas') {
    if (typeof input.formula_r1c1 === 'string') return `${input.formula_r1c1}\nfilled over ${String(input.address ?? '')}`;
    if (Array.isArray(input.formulas)) return gridLines(input.formulas as unknown[][], 6);
  }
  if (n === 'excel_write_cells' && input.cells && typeof input.cells === 'object' && !Array.isArray(input.cells)) {
    // One cell per line, address first: that is how a modeller checks a scattered write.
    const entries = Object.entries(input.cells as Record<string, unknown>);
    const head = entries.slice(0, 8).map(([a, v]) => `${a}  ${scalar(v)}`).join('\n');
    return entries.length > 8 ? `${head}\n… ${entries.length - 8} more` : head;
  }
  if (n === 'excel_copy_range') {
    const from = `${String(input.source_sheet ?? '')}!${String(input.source ?? '')}`;
    const to = `${String(input.dest_sheet ?? '')}!${String(input.dest ?? '')}`;
    const how = input.paste && input.paste !== 'all' ? ` (${String(input.paste)})` : '';
    return `${from}\n  → ${to}${how}`;
  }
  if (n === 'excel_insert_rows' || n === 'excel_insert_columns') {
    const unit = n === 'excel_insert_rows' ? 'row' : 'column';
    const count = Number(input.count ?? 1);
    return `${count} ${unit}${count > 1 ? 's' : ''} at ${String(input.at ?? '')}`;
  }
  if (n === 'excel_delete_rows' || n === 'excel_delete_columns') {
    return `${n === 'excel_delete_rows' ? 'rows' : 'columns'} ${String(input.address ?? '')}`;
  }
  if (n === 'excel_define_name') {
    return `${String(input.name ?? '')} → ${String(input.address ?? '')}${input.scope === 'sheet' ? ' (sheet-scoped)' : ''}`;
  }
  if (n === 'excel_insert_model') {
    // The generic dump would print every answer and every read-back cell as JSON: what the modeller checks before
    // approving is which template, which technology, how many answers are defaults, and the sheets it adds.
    const answers = input.inputs && typeof input.inputs === 'object' ? Object.keys(input.inputs).length : 0;
    const defaults = Array.isArray(input.defaulted) ? input.defaulted.length : 0;
    const sheets = Array.isArray(input.sheets) ? (input.sheets as unknown[]).map(String).join(', ') : '';
    return `${String(input.template ?? '')} · ${String(input.technology ?? '')}\n${answers} inputs (${defaults} defaulted)${sheets ? `\nsheets: ${sheets}` : ''}`;
  }
  if (n === 'excel_set_row_heights') {
    return `rows ${String(input.rows ?? '')} → ${String(input.height ?? '')}`;
  }
  if (n === 'undo_operation') {
    // The one card of an undo batch: what it takes back, in the words the server counted.
    const count = (k: string, one: string, many: string) => {
      const v = Number(input[k] ?? 0);
      return v ? `${v} ${v === 1 ? one : many}` : '';
    };
    const parts = [count('restored', 'range restored', 'ranges restored'), count('names', 'name removed', 'names removed'), count('bands', 'inserted band deleted', 'inserted bands deleted')].filter(Boolean);
    const what = input.name ? `'${String(input.name)}'` : `the last ${String(input.operation ?? 'operation')}`;
    const sheets = Array.isArray(input.sheets) && input.sheets.length ? `\nsheets: ${(input.sheets as unknown[]).map(String).join(', ')}` : '';
    const left = Array.isArray(input.left) && input.left.length ? `\nleft as is: ${(input.left as unknown[]).map(String).join('; ')}` : '';
    return `${what} taken back\n${parts.join(' · ')}${sheets}${left}`;
  }
  const skip = new Set(['sheet', 'address', 'dest_sheet', 'cell']);
  const lines: string[] = [];
  for (const [k, v] of Object.entries(input)) {
    if (skip.has(k) || v === null || v === undefined || v === '' || v === false) continue;
    lines.push(`${k}: ${scalar(v)}`);
  }
  return lines.join('\n') || toolLabel(n);
}

function gridLines(grid: unknown[][], maxRows: number): string {
  return grid
    .slice(0, maxRows)
    .map((row) => (Array.isArray(row) ? row.map(scalar).join(' · ') : scalar(row)))
    .join('\n');
}

function scalar(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  const s = JSON.stringify(v);
  return s.length > 120 ? `${s.slice(0, 117)}…` : s;
}
